import {makeAutoObservable, toJS} from 'mobx';
import {persistence, StorageAdapter} from 'mobx-persist-store';
import {writeStore, readStore} from 'helpers/mobx-persist';
import axios, {AxiosPromise, AxiosResponse, AxiosError} from 'axios';
import UserStore, {UserObject, Password} from './UserStore';
import {ErrorObject} from 'types';

export interface TokenObject {
  token: string;
  expires: number;
}

export type TokenState = TokenObject | undefined;

export type SignInWithUsernameAndPasswordData
  = Pick<UserObject, | 'username'> & {
  password: Password;
};

export type SignUpData
  = Pick<UserObject,
    | 'username'
    | 'email'
    | 'name'> & {
  password: Password;
  isTeacher: boolean;
};

/**
 * This class stores authentication information
 */
export class AuthStore {
  private _token: TokenState;

  /**
   * Make store observable
   */
  constructor() {
    makeAutoObservable(this);

    /* todo: Доработать эту часть. Перед загрузкой хранилища есть задержка. */
    axios.interceptors.request.use((config) => {
      config.headers.Authorization = `Bearer ${this.token?.token}`;
      return config;
    });
  }

  /**
   * Get the token
   * @return {TokenState}
   */
  get token(): TokenState {
    return this._token;
  }

  /**
   * Set the token
   * @param {TokenState} token
   */
  set token(token: TokenState) {
    this._token = token;
  }

  /**
   * This function gets the user profile
   * @param {TokenObject} token
   * @return {Promise<void>}
   * @protected
   */
  async fetch(token: TokenObject) {
    this.token = token;

    axios.interceptors.request.use((config) => {
      config.headers.Authorization = `Bearer ${token.token}`;
      return config;
    });

    return axios({
      method: 'GET',
      url: `${process.env.REACT_APP_API_BASE_URL}/user`,
    }).then((response: AxiosResponse<UserObject>) => (
      UserStore.state = response.data
    )).catch((error: AxiosError<ErrorObject>) => (
      error.response?.data
    ));
  }

  /**
   * This function authorizes the user in the system
   * and receives the profile data
   * @param {SignInWithUsernameAndPasswordData} data
   * @return {AxiosPromise<TokenObject>}
   */
  public async signInWithUsernameAndPassword(
      data: SignInWithUsernameAndPasswordData,
  ) {
    return axios({
      method: 'POST',
      url: `${process.env.REACT_APP_API_BASE_URL}/auth/login`,
      data: data,
    }).then((response: AxiosResponse<TokenObject>) => (
      this.fetch(response.data)
    )).catch((error: AxiosError<ErrorObject>) => (
      error.response?.data
    ));
  }

  /**
   * This function sign up the user in the system
   * and receives the profile data
   * @param {SignUpData} data
   * @return {AxiosPromise<TokenObject>}
   */
  public async signUp(data: SignUpData) {
    return axios({
      method: 'POST',
      url: `${process.env.REACT_APP_API_BASE_URL}/auth/register`,
      data: data,
    }).then((response: AxiosResponse<TokenObject>) => (
      this.fetch(response.data)
    )).catch((error: AxiosError<ErrorObject>) => (
      error.response?.data
    ));
  }

  /**
   * Is logged.
   * @return {boolean}
   */
  public isLogged(): boolean {
    return this.token !== undefined;
  }
}

export default persistence({
  name: 'AuthStore',
  properties: ['_token'],
  adapter: new StorageAdapter({
    read: readStore,
    write: writeStore,
  }),
})(new AuthStore());
